<template>
  <li
    class="list-item"
    :class="{
      [`list-item--flair-${flair}`]: true,
      [`list-item--size-${size}`]: true,
      'list-item--selected': selected,
      'list-item--focused': focused,
      'list-item--active': active,
      'list-item--disabled': disabled,
      'list-item--secondary': secondary,
      'list-item--negative': negative,
    }"
    data-test="list-item"
    :data-selected="selected"
    :data-active="active"
    :disabled="disabled"
    tabindex="0"
    v-bind="$attrs"
    :role="role"
  >
    <div class="list-item__content">
      <div
        v-if="$slots['prepend']"
        class="list-item__content__prepend"
      >
        <slot name="prepend" />
      </div>
      <div
        class="list-item__content__label"
        :class="{
          'list-item__content__label--wrap': wrap,
          'list-item__content__label--full-width': fullWidth,
        }"
      >
        <slot />
      </div>
      <div
        v-if="$slots['append']"
        class="list-item__content__append"
      >
        <slot name="append" />
      </div>
    </div>
  </li>
</template>

<script setup lang="ts">
import type { DarwinFlairType, DarwinSizeType } from '@/uiKit/types'
import { computed } from 'vue'
import { useAttrs } from 'vue'

withDefaults(
  defineProps<{
    flair?: DarwinFlairType
    size?: DarwinSizeType
    fullWidth?: boolean
    selected?: boolean
    active?: boolean
    focused?: boolean
    disabled?: boolean
    negative?: boolean
    secondary?: boolean
    wrap?: boolean
  }>(),
  {
    flair: 'soft',
    size: 'auto',
    fullWidth: false,
    selected: false,
    active: false,
    focused: false,
    disabled: false,
    negative: false,
    secondary: false,
    wrap: false,
  },
)

const attrs = useAttrs()
const role = computed(() => (attrs.role || attrs.ariaRole) as string | undefined)
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/colors.global.scss';
@import '@/uiKit/assets/borders.scss';
@import '@/uiKit/assets/heights.scss';
@import '@/uiKit/assets/spacings.scss';
@import '@/uiKit/assets/typography.scss';

.list-item {
  @include row--distributed;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border-radius: $borderRadius8;
  padding: $spacing-3;
  margin: 0;
  outline: none;
  transition: background 125ms ease;
  color: var(--color-text-subtle);
  cursor: pointer;

  &--flair {
    &-squared {
      border-radius: $borderRadius0;
    }

    &-soft {
      border-radius: $borderRadius8;
    }

    &-rounded {
      border-radius: $borderRadius100;
    }
  }

  &--size {
    &-auto {
      height: fit-content;
    }

    &-xs {
      height: 28px;
    }

    &-sm {
      height: 32px;
    }

    &-md {
      height: 36px;
    }

    &-lg {
      height: 40px;
    }
  }

  &--disabled {
    cursor: not-allowed;
    pointer-events: none;

    .list-item__content__label {
      color: var(--color-text-disabled);
    }
  }

  &:hover:not(.list-item--disabled):not(.list-item--negative):not([data-selected='true']) {
    background-color: var(--color-background-transparent-hovered);
    color: var(--color-icon-default);
  }

  &:hover:not(.list-item--disabled).list-item--negative {
    background-color: var(--color-background-transparent-hovered);
  }

  &.list-item--negative {
    .list-item__content__label,
    .list-item__content__prepend {
      color: var(--color-text-critical);
    }
  }

  &.list-item--secondary {
    .list-item__content__label {
      color: var(--color-text-subtlest);
    }

    &:hover:not(.list-item--disabled) {
      background-color: var(--color-background-transparent-hovered);

      .list-item__content__label {
        color: var(--color-text-default);
      }
    }
  }

  &:active:not(.list-item--disabled) {
    background: var(--color-background-selected);
  }

  &[data-selected='true'] {
    background: var(--color-background-selected);

    &.list-element__label {
      color: var(--color-text-selected);
    }
  }

  &[data-active='true'] {
    background-color: var(--color-background-selected);

    &.list-element__label {
      color: var(--color-text-selected);
    }
  }

  &.list-item--focused {
    background-color: var(--color-background-transparent-hovered);
  }

  &:last-child {
    margin-bottom: 0;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: 4px;

    &__label {
      flex: 1;
      @include typographyRegularBody200;

      text-align: left;
      color: var(--color-text-default);

      width: fit-content;
      @include truncate;

      &--wrap {
        white-space: normal;
      }

      &--full-width {
        width: 100%;
      }
    }

    &__prepend,
    &__append {
      @include col--center;
      height: 20px;
      min-width: 20px;
      width: 20px;

      .darwin-icon {
        font-size: 20px;
      }
    }

    &__prepend {
      display: flex;
      margin-right: 4px;
    }

    &__append {
      display: flex;
      justify-content: center;
      align-items: flex-end;
    }
  }
}
</style>
